import { useRef, useEffect, useState, KeyboardEvent } from 'react';

interface InputAreaProps {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    disabled: boolean;
}

/**
 * InputArea - Glass-styled textarea with auto-resize and send button
 */
export default function InputArea({
    value,
    onChange,
    onSubmit,
    disabled,
}: InputAreaProps) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [placeholder, setPlaceholder] = useState(
        'Faça uma pergunta, viajante. O Oráculo responde...'
    );

    // Update placeholder based on screen width
    useEffect(() => {
        const updatePlaceholder = () => {
            if (window.innerWidth <= 480) {
                setPlaceholder('Faça uma pergunta, viajante.\nO Oráculo responde...');
            } else {
                setPlaceholder('Faça uma pergunta, viajante. O Oráculo responde...');
            }
        };

        updatePlaceholder();
        window.addEventListener('resize', updatePlaceholder);
        return () => window.removeEventListener('resize', updatePlaceholder);
    }, []);

    // Auto-resize textarea (max 3 lines)
    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        textarea.style.height = 'auto';
        const lineHeight = 22;
        const maxHeight = lineHeight * 3;

        if (textarea.scrollHeight <= maxHeight) {
            textarea.style.height = `${Math.max(lineHeight, textarea.scrollHeight)}px`;
        } else {
            textarea.style.height = `${maxHeight}px`;
        }
    }, [value, placeholder]);

    // Handle Enter key (submit without shift)
    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (!disabled && value.trim()) {
                onSubmit();
            }
        }
    };

    return (
        <div className="input-wrapper">
            <div className="input-glass">
                <textarea
                    ref={textareaRef}
                    id="questionInput"
                    className="question-input"
                    placeholder={placeholder}
                    rows={2}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                />
                <div className="input-actions">
                    <button
                        id="sendButton"
                        className="send-button"
                        aria-label="Enviar pergunta"
                        onClick={onSubmit}
                        disabled={disabled || !value.trim()}
                    >
                        <i className="fas fa-arrow-up"></i>
                    </button>
                </div>
            </div>
        </div>
    );
}
